import React, { useEffect, useState, useContext, useRef } from "react";
import PropTypes from "prop-types";
import Switch from "@mui/material/Switch";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import { Icon } from "@iconify/react";

import { toast } from "sonner";
import * as Yup from "yup";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import {
  removeMember,
  useGetPeople,
  useGetPeoplesDetails,
} from "../api/settings";
import { AuthContext } from "../context/AuthContextProvider";
import permissionsData from "../data/UserPermissions.json";
import CountryCodePicker from "./CountryCodePicker";
import CentersTreeView from "./CentersTreeView";
import FormTextField from "./Reusables/FormControls/FormTextFeilds";
import { LoadingButton } from "@mui/lab";
import {
  getPhoneNumberLimit,
  getPhoneNumberLimitMessage,
  sanitizePhoneNumber,
} from "../utils/phoneNumber";

import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";

//
const DropdownIcon = (props) => (
  <Icon
    icon="eva:arrow-ios-downward-fill"
    width="20"
    height="20"
    {...props} // ← IMPORTANT
  />
);
const AddMember = ({
  type = "add",
  handleAddMember,
  userId = null,
  preselectedCenterId,
  preselectedCenterType,
}) => {
  // Aliased: `user` below is this form's own model, not the signed-in account.
  const { all_centres, user: authUser } = useContext(AuthContext);
  // Resetting a member's password is the business owner's alone - an admin can
  // edit a member but not change their password. Enforced in the API too
  // (updateMember), this only keeps the control out of sight.
  const canResetPassword = authUser?.userType === "b2b";
  const { peoplesDetails, isLoading } = useGetPeoplesDetails(userId);

  const [user, setUser] = useState({
    phoneNumber: "",
    virtualId: "",
    defaultPassword: "",
    countryCode: "+91",
  });
  const [errors, setErrors] = useState({});
  const [buttonLoading, setButtonLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [openRemoveDialog, setOpenRemoveDialog] = useState(false);
  const [openResetPassword, setOpenResetPassword] = useState(false);
  const [rememberPassword, setRememberPassword] = useState(false);

  const [passwordData, setPasswordData] = useState({
    password: "",
    confirmPassword: "",
  });

  const [passwordErrors, setPasswordErrors] = useState({});

  const prevRoleRef = useRef(user?.role);

  const [Permissions, setPermissions] = useState(permissionsData);
  let permissionHeaderRendered = false;
  const isDisabled = type === "view" || type === "edit";
  const phoneLimit = getPhoneNumberLimit(user.countryCode || "+91");

  const baseUserSchema = Yup.object()
    .shape({
      firstName: Yup.string()
        .trim()
        .required("First Name is required")
        .min(2, "Too Short!")
        .max(150, "cannot exceed 150 characters!"),
      lastName: Yup.string()
        .trim()
        .max(150, "cannot exceed 150 characters!"),
      email: Yup.string()
        .trim()
        .nullable()
        .test(
          "valid-email",
          "Enter a valid email address",
          (value) => {
            if (!value) return true;
            return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
              value
            );
          }
        ),
      designation: Yup.string()
        .trim()
        .nullable()
        .max(150, "Designation cannot exceed 150 characters"),
      // phoneNumber: Yup.string().required("Phone Number is required"),
      phoneNumber: Yup.string().test(
        "country-phone-limit",
        getPhoneNumberLimitMessage(user.countryCode || "+91"),
        (value) =>
          !value ||
          value.length === getPhoneNumberLimit(user.countryCode || "+91"),
      ),
      virtualId: Yup.string(),
      role: Yup.string().required("Role is required"),
      centerIds: Yup.array()
        .min(1, "Please select at least one centre")
        .required("Please select centre"),
    })
    .test(
      "phone-or-virtual",
      "Either Mobile Number or Virtual ID is required",
      (obj) => obj.phoneNumber || obj.virtualId,
    );

  const addUserSchema = baseUserSchema.shape({
    defaultPassword: Yup.string()
      .required("Password is required")
      .min(10, "Minimum 10 characters")
      .matches(/[A-Z]/, "At least one uppercase letter")
      .matches(/[a-z]/, "At least one lowercase letter")
      .matches(/[0-9]/, "At least one number")
      .matches(/[@$!%*?&#]/, "At least one special character"),
  });

  const getSubmitSchema = () =>
    type === "add" ? addUserSchema : baseUserSchema;

  const normalizeCenterIds = (centerIds = []) =>
    centerIds
      .map((center) =>
        typeof center === "object" && center !== null
          ? center.id || center._id || center.value
          : center,
      )
      .filter(Boolean);

  const resetPasswordSchema = Yup.object({
    password: Yup.string()
      .required("Password is required")
      .min(10, "Minimum 10 characters")
      .matches(/[A-Z]/, "At least one uppercase letter")
      .matches(/[a-z]/, "At least one lowercase letter")
      .matches(/[0-9]/, "At least one number")
      .matches(/[@$!%*?&#]/, "At least one special character"),

    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords do not match")
      .required("Confirm password is required"),
  });
  useEffect(() => {
    if (!window) return;
    const container = document.querySelector(".page-wrapper"); // your scrollable div
    if (container) {
      container.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (peoplesDetails) {
      setUser({
        firstName: peoplesDetails?.user?.firstName || "",
        middleName: peoplesDetails?.user?.middleName || "",
        lastName: peoplesDetails?.user?.lastName || "",
        email: peoplesDetails?.user?.email || "",
        phoneNumber: peoplesDetails?.user?.phoneNo || "",
        role: peoplesDetails?.user?.role || "",
        designation: peoplesDetails?.user?.designation || "",
        centerIds: peoplesDetails?.centerDetails || [],
        userId: peoplesDetails?.userId || "",
        countryCode: peoplesDetails?.user?.countryCode || "",
        virtualId: peoplesDetails?.user?.virtualId || "",
        defaultPassword: peoplesDetails?.user?.password || "",
      });
    }
  }, [peoplesDetails]);
  // useEffect(() => {
  //   if (preselectedCenterId) {
  //     setUser((prev) => ({
  //       ...prev,
  //       centerIds: [preselectedCenterId],
  //     }));
  //   }
  // }, [preselectedCenterId]);

  useEffect(() => {
    if (type !== "add") return;

    const savedPassword = localStorage.getItem("member_default_password");
    const remember =
      localStorage.getItem("remember_member_password") === "true";

    setRememberPassword(remember);

    if (remember && savedPassword) {
      setUser((prev) => ({
        ...prev,
        defaultPassword: savedPassword,
      }));
    }
  }, [type]);

  const convertPermissionsForSubmit = () => {
    const permissions = {};

    const isDoctor = user.role === "b2b-doctor";

    Permissions.forEach((section) => {
      // For doctor, include sections: 'cases' + 'ecgInterpretation' + 'ecgCollectionAndReports'
      if (
        isDoctor &&
        ![
          "cases",
          "ecgInterpretation",
          "ecgCollectionAndReports",
          "applicationUses",
        ].includes(section.key)
      ) {
        return;
      }

      // For non-doctors, skip 'cases' section
      if (!isDoctor && section.key === "cases") {
        return;
      }

      permissions[section.key] = {};
      section.items.forEach((item) => {
        permissions[section.key][item.subkey] = item.type;
      });

      // if (isDoctor && section.key === "cases") {
      //   const canRefer = permissions[section.key]["canReferCases"];
      //   const canView = permissions[section.key]["canViewCases"];

      //   if (canRefer || canView) {
      //     permissions[section.key]["canReferCases"] = true;
      //     permissions[section.key]["canViewCases"] = true;
      //   }
      // }
    });

    // --- Cross-permission dependencies ---

    // For non-doctors, Share ECG + Send for Interpretation => View ECG reports.
    // Doctors control report access only through canViewReports.
    const shareReports = permissions.ecgCollectionAndReports?.canShareReports;
    const sendForInterpretation =
      permissions.ecgInterpretation?.canSendForInterpretation;
    if (!isDoctor && shareReports && sendForInterpretation) {
      permissions.ecgCollectionAndReports ??= {};
      permissions.ecgCollectionAndReports.canViewReports = true;
    }

    // Create/Delete collection requests => View collection requests
    // if (permissions.ecgCollectionAndReports?.canManageRequests) {
    //   permissions.ecgCollectionAndReports.canViewRequests = true;
    // }

    // Take ECG test => grant View + Manage collection requests
    // if (permissions.applicationUses?.canAccessApp) {
    //   permissions.ecgCollectionAndReports ??= {};
    //   permissions.ecgCollectionAndReports.canManageRequests = true;
    //   permissions.ecgCollectionAndReports.canViewRequests = true;
    // }
    if (permissions.applicationUses?.canAccessApp) {
      permissions.ecgCollectionAndReports ??= {};

      if (permissions.ecgCollectionAndReports.canManageRequests) {
        permissions.ecgCollectionAndReports.canViewRequests = true;
      }
    }
    permissions.ecgCollectionAndReports ??= {};
    permissions.ecgCollectionAndReports.canShareReports = true;

    return permissions;
  };

  const handleSubmitUser = async () => {
    try {
      const submitUser = {
        ...user,
        centerIds: normalizeCenterIds(user.centerIds),
      };

      await getSubmitSchema().validate(submitUser, { abortEarly: false });

      if (rememberPassword) {
        localStorage.setItem(
          "member_default_password",
          submitUser.defaultPassword,
        );
      } else {
        localStorage.removeItem("member_default_password");
      }
      setErrors({});
      const data = {
        ...submitUser,
        permissions: convertPermissionsForSubmit(),
      };
      setButtonLoading(true);
      await handleAddMember(data);
    } catch (err) {
      const formattedErrors = {};
      (err.inner || []).forEach((e) => {
        if (e.path) {
          formattedErrors[e.path] = e.message;
        } else {
          formattedErrors.general = e.message;
        }
      });
      if (!err.inner) {
        formattedErrors.general = err.message;
      }
      setErrors(formattedErrors);
    } finally {
      setButtonLoading(false);
    }
  };
  const handleResetPassword = async () => {
    // Unreachable through the UI for a non-owner, but this is the function that
    // actually sends the new password - so it checks rather than trusting that
    // whoever called it was allowed to.
    if (!canResetPassword) {
      toast.error("You are not authorized to reset this user's password.");
      setOpenResetPassword(false);
      return;
    }

    try {
      await resetPasswordSchema.validate(passwordData, {
        abortEarly: false,
      });

      setPasswordErrors({});
      setButtonLoading(true);

      await handleAddMember({
        ...user,
        defaultPassword: passwordData.password,
        permissions: convertPermissionsForSubmit(),
      });

      setOpenResetPassword(false);

      setPasswordData({
        password: "",
        confirmPassword: "",
      });
    } catch (err) {
      if (err.inner) {
        const errors = {};
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
        setPasswordErrors(errors);
      } else {
        toast.error(err.message);
      }
    } finally {
      setButtonLoading(false);
    }
  };
  const handleDeleteMember = async () => {
    try {
      const response = await removeMember({
        userId: peoplesDetails?.userId,
        centerIds: peoplesDetails?.centerDetails,
      });

      if (response.success) {
        handleAddMember(null);
        toast.success(response.message);
      } else {
        toast.error(response.message);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleSelectMember = (item) => {
    setSearchTerm("");
    setUser({
      firstName: item?.user?.firstName,
      lastName: item?.user?.lastName,
      phoneNumber: item?.user?.phoneNumber,
      countryCode: item?.user?.countryCode || "+91",
      email: item?.user?.email,
      designation: item?.user?.designation,
    });
  };

  // Permissions from peoplesDetails data
  useEffect(() => {
    if (!peoplesDetails?.permissions) return;

    const permissionKeyMap = {
      canViewEcgReports: "canViewReports",
      canShareEcgReports: "canShareReports",
      canInterpretReports: "canInterpretReports",
    };

    const updatedPermissions = permissionsData.map((section) => {
      const sectionData = peoplesDetails.permissions[section.key] || {};

      const updatedItems = section.items.map((item) => {
        const actualKey = permissionKeyMap[item.subkey] || item.subkey;
        return {
          ...item,
          type: sectionData[actualKey] ?? false,
        };
      });

      return { ...section, items: updatedItems };
    });

    setPermissions(updatedPermissions);
  }, [peoplesDetails]);

  // Handle auto-permission change based on role

  const handleRoleChange = (e) => {
    let role = e.target.value;
    setUser({ ...user, role: role });
    if (!role || prevRoleRef.current === role) return;
    prevRoleRef.current = role;

    if (role === "b2b-admin") {
      const updatedPermissions = Permissions.map((section) => ({
        ...section,
        items: section.items.map((item) => ({ ...item, type: true })),
      }));
      setPermissions(updatedPermissions);

      setUser((prev) => ({
        ...prev,
        centerIds: all_centres.flatMap((c) => [
          c.value,
          ...(c.spokeCenters?.map((s) => s.id) || []),
        ]),
      }));
    }

    if (role === "b2b-member" || role === "b2b-admin") {
      let updatedPermissions;
      if (role === "b2b-admin") {
        updatedPermissions = Permissions.map((section) => ({
          ...section,
          items: section.items.map((item) => ({ ...item, type: true })),
        }));
      } else {
        updatedPermissions = Permissions.map((section) => ({
          ...section,
          items: section.items.map((item) => ({ ...item, type: false })),
        }));
      }

      setPermissions(updatedPermissions);

      setUser((prev) => ({
        ...prev,
        centerIds: [],
      }));
    }

    if (role === "b2b-doctor") {
      const updatedPermissions = Permissions.map((section) => ({
        ...section,
        items: section.items.map((item) => ({
          ...item,
          type: [
            "cases",
            "ecgInterpretation",
            "ecgCollectionAndReports",
          ].includes(section.key)
            ? item.type
            : false,
        })),
      }));
      setPermissions(updatedPermissions);
    }
    if (role === "b2b-viewer") {
      const updated = Permissions.map((section) => ({
        ...section,
        items: section.items.map((item) => ({ ...item, type: false })), // all disabled
      }));
      setPermissions(updated);
    }
  };

  //
  return (
    <>
      {!isLoading && (
        <>
          <div className="card2 pt-20 mt-20" style={{ padding: "20px" }}>
            <div className="row ">
              {type != "addcenter" && (
                <>
                  <Typography
                    variant="h5"
                    component="h5"
                    sx={{
                      fontWeight: 700, // bold
                      fontFamily: "Figtree", // custom font
                      color: "var(--app-text)",
                      marginBottom: "20px", // optional text color
                    }}
                  >
                    Personal Information
                  </Typography>
                </>
              )}
              {type == "addcenter" && (
                <>
                  <div className="col-12 mb-4 mt-4">
                    <div
                      className="form-group"
                      style={{ position: "relative" }}
                    >
                      <Icon
                        icon="mdi:search"
                        width={25}
                        height={25}
                        style={{
                          position: "absolute",
                          top: "50%",
                          left: "10px",
                          transform: "translateY(-50%)",
                          color: "var(--app-text-muted)",
                          pointerEvents: "none",
                        }}
                      />
                      <input
                        id="searchMember"
                        type="text"
                        placeholder="Search member by name or mobile number"
                        className="form-control"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{
                          width: "100%",
                          padding: "10px 10px 10px 36px",
                          fontSize: "16px",
                          border: "1px solid var(--app-border)",
                          borderRadius: "4px",
                        }}
                      />
                    </div>
                    {filteredData.length > 0 && (
                      <ul
                        style={{
                          listStyleType: "none",
                          padding: 0,
                          marginTop: "8px",
                          border: "1px solid var(--app-border)",
                          borderRadius: "8px",
                          maxHeight: "150px",
                          overflowY: "auto",
                          background: "var(--app-surface)",
                        }}
                      >
                        {filteredData.map((item, index) => (
                          <li
                            key={index}
                            style={{
                              padding: 0,
                              borderBottom: "1px solid var(--app-border)",
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleSelectMember(item)}
                              className="w-100 text-start"
                              style={{
                                padding: "8px",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              <div
                                className="d-flex align-items-center"
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "600",
                                }}
                              >
                                <Avatar
                                  src={item.profile}
                                  alt=""
                                  sx={{ width: 30, height: 30 }}
                                />
                                <div className="d-flex flex-column ms-2">
                                  <span>
                                    {item.user?.firstName} {item.user?.lastName}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      fontWeight: "400",
                                      color: "var(--app-text-muted)",
                                    }}
                                  >
                                    {item.user?.phoneNumber}
                                  </span>
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}

                        {/* {filteredData.map((item, index) => (
            <li
              key={index}
              style={{
                padding: "8px",
                borderBottom: "1px solid var(--app-border)",
                cursor: "pointer",
              }}
              onClick={() => setSearchTerm(item)}
            >
              {item}
            </li>
          ))} */}
                      </ul>
                    )}

                    {filteredData.length === 0 && searchTerm !== "" && (
                      <div
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid var(--app-border)",
                          cursor: "pointer",
                          fontSize: "15px",
                        }}
                      >
                        No results found.
                      </div>
                    )}
                  </div>
                </>
              )}

              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 18%" },
                  marginBottom: "20px",
                }}
              >
                <FormTextField
                  label="First Name"
                  name="firstName"
                  value={user.firstName}
                  onChange={(e) =>
                    setUser({ ...user, firstName: e.target.value })
                  }
                  errors={errors?.firstName}
                  required
                  placeholder="Enter First Name"
                  disabled={type === "view"}
                />
              </Box>

              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 18%" },
                  marginBottom: "20px",
                }}
              >
                <FormTextField
                  label="Last Name"
                  name="lastName"
                  value={user.lastName}
                  onChange={(e) =>
                    setUser({ ...user, lastName: e.target.value })
                  }
                  errors={errors?.lastName}
                  // required
                  placeholder="Enter Last Name"
                  disabled={type === "view"}
                />
              </Box>

              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 50%" },
                  marginBottom: "20px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "15px",
                    fontWeight: 500,
                    color: "var(--app-text-muted)",
                    mb: "6px",
                    fontFamily: "Figtree",
                  }}
                >
                  Contact Number<span style={{ color: "#d32f2f" }}> *</span>
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {/* Country Code */}
                  <Box sx={{ width: "80px" }}>
                    <CountryCodePicker
                      countryCode={user.countryCode}
                      disabled={isDisabled}
                      onChange={(value) =>
                        setUser({
                          ...user,
                          countryCode: value,
                          phoneNumber: sanitizePhoneNumber(
                            user.phoneNumber,
                            value,
                          ),
                        })
                      }
                    />
                  </Box>

                  {/* Phone Input */}
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      id="phoneNumber"
                      value={user.phoneNumber || ""}
                      onChange={(e) => {
                        setUser({
                          ...user,
                          phoneNumber: sanitizePhoneNumber(
                            e.target.value,
                            user.countryCode || "+91",
                          ),
                        });
                      }}
                      placeholder="Enter Contact Number"
                      disabled={isDisabled}
                      size="small"
                      inputProps={{
                        maxLength: phoneLimit,
                        inputMode: "numeric",
                      }}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "48px",
                          borderRadius: "8px",
                          backgroundColor: "var(--app-surface)", // light grey bg
                          fontSize: "14px",

                          "& fieldset": {
                            borderColor: "var(--app-border)", // soft border
                          },
                          "&:hover fieldset": {
                            borderColor: "#D1D5DB",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#6366F1", // subtle purple focus
                          },
                        },

                        "& input::placeholder": {
                          color: "var(--app-text-muted)",
                          opacity: 1,
                          fontSize: "14px",
                        },
                      }}
                    />
                  </Box>

                  {/* OR Divider */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      height: "40px",
                    }}
                  >
                    <Box
                      sx={{
                        width: "1px",
                        height: "100%",
                        bgcolor: "var(--app-border)",
                      }}
                    />
                    <Typography
                      sx={{
                        mx: "6px",
                        fontSize: "11px",
                        color: "var(--app-text-muted)",
                        fontWeight: 500,
                      }}
                    >
                      OR
                    </Typography>
                    <Box
                      sx={{
                        width: "1px",
                        height: "100%",
                        bgcolor: "var(--app-border)",
                      }}
                    />
                  </Box>

                  {/* User ID */}
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      fullWidth
                      id="virtualId"
                      value={user.virtualId || ""}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\s/g, "");
                        setUser({ ...user, virtualId: value });
                      }}
                      inputProps={{ maxLength: 30 }}
                      placeholder="Enter User ID"
                      disabled={isDisabled}
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          height: "48px",
                          borderRadius: "8px",
                          backgroundColor: "var(--app-surface)", // light grey bg
                          fontSize: "14px",

                          "& fieldset": {
                            borderColor: "var(--app-border)", // soft border
                          },
                          "&:hover fieldset": {
                            borderColor: "#D1D5DB",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "#6366F1", // subtle purple focus
                          },
                        },

                        "& input::placeholder": {
                          color: "var(--app-text-muted)",
                          opacity: 1,
                          fontSize: "14px",
                        },
                      }}
                    />
                  </Box>
                </Box>
                {(errors.phoneNumber || errors.virtualId || errors.general) && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      mt: "4px",
                    }}
                  >
                    {errors.phoneNumber || errors.virtualId || errors.general}
                  </Typography>
                )}
              </Box>
              {/* <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 32%" },
                  marginBottom: "20px",
                }}
              >
                <FormTextField
                  label="Default Password"
                  name="defaultPassword"
                  value={user?.defaultPassword}
                  onChange={(e) => setUser({ ...user, defaultPassword: e.target.value })}
                  errors={errors?.defaultPassword}
                  required
                  placeholder="Enter Default Password"
                  disabled={type === "view"}
                />
              </Box> */}
              {type === "add" ? (
                <Box
                  sx={{
                    flex: { xs: "1 1 100%", md: "1 1 32%" },
                    marginBottom: "20px",
                  }}
                >
                  <FormTextField
                    type="password"
                    label="Default Password"
                    value={user.defaultPassword}
                    onChange={(e) =>
                      setUser({ ...user, defaultPassword: e.target.value })
                    }
                    errors={errors?.defaultPassword}
                    required
                    placeholder="Enter Default Password"
                  />

                  <FormControlLabel
                    sx={{ mt: 0.5, ml: 0 }}
                    control={
                      <Checkbox
                        size="small"
                        checked={rememberPassword}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setRememberPassword(checked);

                          localStorage.setItem(
                            "remember_member_password",
                            checked.toString(),
                          );

                          if (!checked) {
                            localStorage.removeItem("member_default_password");
                          }
                        }}
                      />
                    }
                    label={
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "var(--app-text-muted)",
                        }}
                      >
                        Remember password for next member
                      </Typography>
                    }
                  />
                </Box>
              ) : type === "edit" ? (
                <Box
                  sx={{
                    flex: { xs: "1 1 100%", md: "1 1 32%" },
                    marginBottom: "20px",
                  }}
                >
                  <FormTextField
                    label="Default Password"
                    value="••••••••••••"
                    disabled
                    required
                  />

                  {/* The masked field above stays for everyone - it only says a
                      password is set. The reset action is the owner's alone. */}
                  {canResetPassword && (
                    <Typography
                      onClick={() => setOpenResetPassword(true)}
                      sx={{
                        mt: 0.75,
                        fontSize: "13px",
                        color: "#5A4FFA",
                        cursor: "pointer",
                        fontWeight: 500,
                        width: "fit-content",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                    >
                      Reset Password
                    </Typography>
                  )}
                </Box>
              ) : null}

              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 32%" },
                  marginBottom: "20px",
                }}
              >
                <FormTextField
                  label="Email Address"
                  name="email"
                  value={user.email}
                  onChange={(e) => setUser({ ...user, email: e.target.value })}
                  errors={errors?.email}
                  // required
                  placeholder="Enter Email Address"
                  disabled={type === "view"}
                />
              </Box>

              <Box
                sx={{
                  flex: { xs: "1 1 100%", md: "1 1 32%" },
                  marginBottom: "20px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "var(--app-text-muted)",
                    mb: "6px",
                    fontFamily: "Figtree",
                  }}
                >
                  Role <span style={{ color: "#d32f2f" }}>*</span>
                </Typography>

                <FormControl
                  fullWidth
                  size="small"
                  error={!!errors.role}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "48px",
                      borderRadius: "10px",
                      fontFamily: "Figtree",
                      backgroundColor: "var(--app-surface)",

                      "& fieldset": {
                        borderColor: "var(--app-border)",
                      },
                      "&:hover fieldset": {
                        borderColor: "var(--app-border)",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--app-border)",
                        boxShadow: "none",
                      },
                    },

                    "& .MuiSelect-select": {
                      padding: "12px 14px",
                      display: "flex",
                      alignItems: "center",
                      fontSize: "15px",
                      fontWeight: 500,
                      color: user.role
                        ? "var(--app-text)"
                        : "var(--app-text-muted)",
                    },
                  }}
                >
                  <Select
                    value={user.role || ""}
                    onChange={handleRoleChange}
                    IconComponent={DropdownIcon}
                    disabled={type === "view"}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: 400,
                              color: "var(--app-text-muted)",
                            }}
                          >
                            Select Role
                          </span>
                        );
                      }

                      const roleLabels = {
                        "b2b-admin": "Admin",
                        "b2b-member": "Member",
                        "b2b-doctor": "Doctor",
                        "b2b-viewer": "Viewer",
                      };

                      return roleLabels[selected];
                    }}
                  >
                    <MenuItem value="b2b-admin">Admin</MenuItem>
                    <MenuItem value="b2b-member">Member</MenuItem>
                    <MenuItem value="b2b-doctor">Doctor</MenuItem>
                    <MenuItem value="b2b-viewer">Viewer</MenuItem>
                  </Select>
                </FormControl>

                {errors.role && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      mt: "4px",
                    }}
                  >
                    {errors.role}
                  </Typography>
                )}
              </Box>

              <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 32%" } }}>
                <FormTextField
                  label="Designation"
                  name="designation"
                  value={user.designation}
                  onChange={(e) =>
                    setUser({ ...user, designation: e.target.value })
                  }
                  errors={errors?.designation}
                  // required
                  placeholder="Enter Designation"
                  disabled={type === "view"}
                />
              </Box>
            </div>
          </div>
          <div className="card2 pt-20 mt-20" style={{ padding: "20px" }}>
            <div style={{ width: "100%" }}>
              {type != "addcenter" && (
                <>
                  <Typography
                    variant="h5"
                    component="h5"
                    sx={{
                      fontWeight: 700, // bold
                      fontFamily: "Figtree", // custom font
                      color: "var(--app-text)",
                      marginBottom: "20px", // optional text color
                    }}
                  >
                    Workspaces and Permissions
                  </Typography>

                  <Typography
                    variant="h6" // valid MUI variant
                    component="h6"
                    sx={{
                      fontWeight: 500, // semi-bold
                      fontFamily: "Figtree", // custom font
                      color: "var(--app-text-muted)",
                      margin: "20px 0",
                      fontSize: "15px", // optional: adjust size if needed
                      lineHeight: "100%",
                      letterSpacing: "0%",
                    }}
                  >
                    Select Centres<span style={{ color: "#d32f2f" }}> *</span>
                  </Typography>

                  <CentersTreeView
                    all_centres={all_centres}
                    user={user}
                    setUser={setUser}
                    type={type}
                    orgSettings={false}
                  />
                  {errors.centerIds && (
                    <Typography
                      sx={{ color: "#d32f2f", fontSize: "12px", mt: "4px" }}
                    >
                      {errors.centerIds}
                    </Typography>
                  )}
                  <br />
                </>
              )}

              <div className="row">
                {Permissions.map((section, sIdx) => {
                  const allowedItems = section.items.filter(
                    (item) =>
                      item.role.includes(user.role) &&
                      item.subkey !== "canShareReports" &&
                      !(
                        user.role === "b2b-doctor" &&
                        item.subkey === "canReferCases"
                      ),
                  );

                  if (allowedItems.length === 0) return null;

                  return (
                    <React.Fragment key={section.key}>
                      {/*  HEADING APPEARS ONLY ONCE */}
                      {!permissionHeaderRendered && (
                        <>
                          {(() => {
                            permissionHeaderRendered = true;
                            return (
                              <Typography
                                variant="h6"
                                sx={{
                                  fontWeight: 600, // semi-bold
                                  fontFamily: "Figtree", // custom font
                                  color: "var(--app-text)",
                                  margin: "20px 0",
                                  fontSize: "16px", // optional: adjust size if needed
                                  lineHeight: "100%",
                                  letterSpacing: "0%",
                                }}
                              >
                                Permissions
                              </Typography>
                            );
                          })()}
                        </>
                      )}

                      <div className="col-12">
                        <div className={sIdx !== 0 ? "mt-4" : ""}>
                          <Typography
                            sx={{
                              fontWeight: 700, // semi-bold
                              fontFamily: "Figtree", // custom font
                              color: "var(--app-text)",
                              fontStyle: "Bold",
                              margin: "20px 0",
                              fontSize: "16px", // optional: adjust size if needed
                              lineHeight: "100%",
                              letterSpacing: "0%",
                            }}
                          >
                            {section.title}
                          </Typography>
                        </div>

                        {allowedItems.map((label, iIdx) => (
                          <div
                            className="row align-items-center mt-2"
                            key={`${section.key}-${label.subkey}`}
                          >
                            <div className="col-8">
                              <Typography
                                sx={{
                                  fontWeight: 400, // semi-bold
                                  fontFamily: "Figtree", // custom font
                                  color: "var(--app-text)",
                                  fontStyle: "regular",
                                  fontSize: "16px", // optional: adjust size if needed
                                  lineHeight: "100%",
                                  letterSpacing: "0%",
                                }}
                              >
                                {label.text}
                              </Typography>
                            </div>
                            <div className="col-3">
                              <Switch
                                checked={label.type}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  if (user.role === "b2b-viewer") return;

                                  const updated = Permissions.map((sec) => ({
                                    ...sec,
                                    items: sec.items.map((it) => ({ ...it })),
                                  }));

                                  const findPermission = (subkey) => {
                                    for (const sec of updated) {
                                      const item = sec.items.find(
                                        (i) => i.subkey === subkey,
                                      );
                                      if (item) return item;
                                    }
                                    return null;
                                  };

                                  const current = findPermission(label.subkey);
                                  if (!current) return;

                                  current.type = checked;

                                  // ==========================
                                  // ENABLE DEPENDENCIES
                                  // ==========================

                                  if (checked) {
                                    switch (label.subkey) {
                                      case "canManageRequests":
                                        findPermission("canViewRequests").type =
                                          true;
                                        break;

                                      case "canAccessApp":
                                        findPermission(
                                          "canManageRequests",
                                        ).type = true;
                                        findPermission("canViewRequests").type =
                                          true;
                                        break;

                                      case "canSendForInterpretation":
                                        findPermission("canViewReports").type =
                                          true;
                                        break;
                                    }
                                  }

                                  // ==========================
                                  // DISABLE DEPENDENCIES
                                  // ==========================

                                  const manageReq = findPermission("canManageRequests");
                                  const viewReq = findPermission("canViewRequests");

                                  if (!checked) {
                                    switch (label.subkey) {
                                      case "canAccessApp":
                                        if (manageReq) manageReq.type = false;
                                        if (viewReq) viewReq.type = false;
                                        break;

                                      case "canManageRequests":
                                        if (viewReq) viewReq.type = false;
                                        break;
                                    }
                                  }

                                  if (
                                    label.subkey === "canShareReports" ||
                                    label.subkey === "canSendForInterpretation"
                                  ) {
                                    const ecgSection = updated.find(
                                      (sec) =>
                                        sec.key === "ecgCollectionAndReports",
                                    );
                                    const sendSection = updated.find(
                                      (sec) => sec.key === "ecgInterpretation",
                                    );

                                    const share = ecgSection?.items.find(
                                      (it) => it.subkey === "canShareReports",
                                    )?.type;
                                    const send = sendSection?.items.find(
                                      (it) =>
                                        it.subkey ===
                                        "canSendForInterpretation",
                                    )?.type;

                                    if (
                                      user.role !== "b2b-doctor" &&
                                      share &&
                                      send &&
                                      ecgSection
                                    ) {
                                      const viewReports = ecgSection.items.find(
                                        (it) => it.subkey === "canViewReports",
                                      );
                                      if (viewReports) viewReports.type = true;
                                    }
                                  }

                                  setPermissions(updated);
                                }}
                                disabled={
                                  type === "view" ||
                                  user.role === "b2b-admin" ||
                                  user.role === "b2b-viewer"
                                }
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          <div className={"mt-4"}>
            {type == "add" ? (
              <button
                onClick={() => {
                  handleSubmitUser();
                }}
                disabled={buttonLoading}
                className="btn btn-added-primary"
              >
                {" "}
                Send Invitation{" "}
                {/* <Icon icon="material-symbols:send" width="24" height="24" /> */}
              </button>
            ) : type == "edit" ? (
              <>
                <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                  <LoadingButton
                    loading={buttonLoading}
                    onClick={handleSubmitUser}
                    variant="contained"
                    sx={{
                      background: "linear-gradient(90deg,#5A4FFA,#5751E9)",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "14px",
                      boxShadow: "none",
                      "&:hover": {
                        boxShadow: "none",
                      },
                    }}
                  >
                    Save Details
                  </LoadingButton>

                  <LoadingButton
                    onClick={() => setOpenRemoveDialog(true)}
                    variant="contained"
                    sx={{
                      backgroundColor: "#FDE3E3",
                      color: "#C62828",
                      borderRadius: "8px",
                      px: 2,
                      py: 1,
                      fontWeight: 600,
                      textTransform: "none",
                      fontSize: "14px",
                      boxShadow: "none",
                      "&:hover": {
                        backgroundColor: "#F8D3D3",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Delete Member
                  </LoadingButton>
                </Stack>

                <Dialog
                  open={openRemoveDialog}
                  onClose={() => setOpenRemoveDialog(false)}
                  aria-labelledby="remove-dialog-title"
                  PaperProps={{
                    sx: {
                      borderRadius: "20px",
                      padding: "0px",
                      width: "508px",
                      boxShadow: "var(--app-shadow)",
                      position: "relative",
                      paddingLeft: "15px",
                    },
                  }}
                >
                  {/* Close Icon */}
                  <Icon
                    icon="mdi:close"
                    width="26"
                    height="26"
                    onClick={() => setOpenRemoveDialog(false)}
                    style={{
                      position: "absolute",
                      right: 16,
                      top: 16,
                      cursor: "pointer",
                      color: "#444",
                    }}
                  />

                  <DialogTitle
                    id="remove-dialog-title"
                    sx={{
                      textAlign: "center",
                      fontWeight: "700",
                      fontSize: "22px",
                      mb: 1,
                      mt: 2,
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                    >
                      {/* Warning Icon */}
                      <Icon
                        icon="mdi:alert"
                        width="65"
                        height="65"
                        style={{ color: "#E04A3A", marginBottom: "12px" }}
                      />
                      Delete Member From Organization?
                    </Box>
                  </DialogTitle>

                  <DialogContent sx={{ px: 4 }}>
                    <Typography
                      align="center"
                      variant="body1"
                      sx={{
                        color: "var(--app-text-muted)",
                        fontSize: "16px",
                        lineHeight: "1.6",
                        mt: 1,
                      }}
                    >
                      Are you sure you want to remove
                      <strong>
                        {" "}
                        {user.firstName} {user.lastName}{" "}
                      </strong>
                      from all centres of this Organization?
                    </Typography>
                  </DialogContent>

                  <DialogActions
                    className="spn-model-btn"
                    sx={{
                      justifyContent: "center",
                      mt: 1,
                      gap: 0,
                      pb: 2,
                    }}
                  >
                    {/* Left Button – Light Red */}
                    <Button
                      className="btn btn-added-danger"
                      variant="contained"
                      size="medium"
                      sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        px: 4,
                        backgroundColor: "#FDE3E3",
                        color: "#C62828",
                        fontWeight: "600",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#F8D3D3",
                          boxShadow: "none",
                        },
                      }}
                      onClick={() => {
                        handleDeleteMember();
                        setOpenRemoveDialog(false);
                      }}
                      disabled={buttonLoading}
                    >
                      Yes, Remove
                    </Button>

                    {/* Right Button – Purple */}
                    <Button
                      className="btn btn-added-primary"
                      variant="contained"
                      size="medium"
                      sx={{
                        textTransform: "none",
                        borderRadius: "12px",
                        px: 4,
                        backgroundColor: "#5A4FFA",
                        color: "white",
                        fontWeight: "600",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#4A3EEA",
                          boxShadow: "none",
                        },
                      }}
                      onClick={() => setOpenRemoveDialog(false)}
                    >
                      No, Go Back
                    </Button>
                  </DialogActions>
                </Dialog>
              </>
            ) : type == "view" ? (
              <></>
            ) : (
              <>
                <button
                  disabled={buttonLoading}
                  onClick={() => {
                    handleSubmitUser();
                  }}
                  className="btn btn-added-primary"
                >
                  Save Member
                  {/* <Icon icon="material-symbols:send" width="24" height="24" /> */}
                </button>
                &nbsp;&nbsp;
                <button
                  disabled={buttonLoading}
                  onClick={() => {
                    handleAddMember(null);
                  }}
                  className="btn btn-added-danger"
                >
                  Close
                </button>
              </>
            )}
          </div>
          <Dialog
            // Belt and braces: the only way to open this is the link above,
            // which non-owners never see - but the dialog refuses to open on
            // its own account too, so no future caller can bypass that.
            open={openResetPassword && canResetPassword}
            onClose={() => setOpenResetPassword(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: "20px",
                p: 1,
                boxShadow: "var(--app-shadow)",
              },
            }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: "50%",
                    bgcolor: "#F3F0FF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon
                    icon="mdi:lock-reset"
                    width={34}
                    height={34}
                    color="#5A4FFA"
                  />
                </Box>

                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: "var(--app-text)",
                  }}
                >
                  Reset Password
                </Typography>

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "var(--app-text-muted)",
                    textAlign: "center",
                    maxWidth: 320,
                  }}
                >
                  Enter a new password for this member. The new password will
                  take effect immediately.
                </Typography>
              </Box>
            </DialogTitle>

            <DialogContent sx={{ mt: 2 }}>
              <Stack spacing={2}>
                <FormTextField
                  type="password"
                  label="New Password"
                  value={passwordData.password}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      password: e.target.value,
                    })
                  }
                  errors={passwordErrors.password}
                  required
                  placeholder="Enter new password"
                />

                <FormTextField
                  type="password"
                  label="Confirm Password"
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({
                      ...passwordData,
                      confirmPassword: e.target.value,
                    })
                  }
                  errors={passwordErrors.confirmPassword}
                  required
                  placeholder="Re-enter password"
                />
              </Stack>
            </DialogContent>

            <DialogActions
              sx={{
                px: 3,
                pb: 3,
                justifyContent: "space-between",
              }}
            >
              <Button
                onClick={() => setOpenResetPassword(false)}
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                }}
              >
                Cancel
              </Button>

              <LoadingButton
                loading={buttonLoading}
                variant="contained"
                onClick={handleResetPassword}
                sx={{
                  background: "linear-gradient(90deg,#5A4FFA,#5751E9)",
                  textTransform: "none",
                  borderRadius: "10px",
                  px: 3,
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
              >
                Reset Password
              </LoadingButton>
            </DialogActions>
          </Dialog>
        </>
      )}
    </>
  );
};

export default AddMember;

AddMember.propTypes = {
  type: PropTypes.string,
  handleAddMember: PropTypes.func.isRequired,
  peoplesDetails: PropTypes.object,
};
